import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { currentSurface, isPlayerApp, postLoginPath, profilePath } from '../lib/appSurface';
import { currentTenantOrNull } from '../lib/tenants';
import { useInstall } from '../lib/pwa/install';
import { useBrandName } from '../lib/pwa/brand';
import { usePushSubscription } from '../lib/pwa/push';
import { sendTestPush } from '../lib/api/push';
import { showErrorToast, showSuccessToast } from '../lib/toast';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import {
  Crown,
  ChevronDown,
  LayoutDashboard,
  User,
  LogOut,
  UserCircle,
  Building2,
  Users,
  ListChecks,
  Download,
  Bell,
  BellOff,
  Send,
  LayoutTemplate,
} from 'lucide-react';
import { NotificationsBell } from './NotificationsBell';
import { MessagesLink } from './MessagesLink';
import { InstallInstructionsModal } from './InstallInstructionsModal';
import { LocalePicker } from './LocalePicker';
import { useOrgProfile } from '../lib/useOrgProfile';
import { useTranslation } from 'react-i18next';

// Build-time brand name baked into the bundle (same VITE_APP_NAME the
// htmlBranding() Vite plugin substitutes into <title>). On branded
// builds it lets the header render the right name on the very first
// paint, before the org-profile fetch resolves — no "Swiss Magic" flash.
const BUILD_APP_NAME = (import.meta.env.VITE_APP_NAME ?? '').trim();

export function AppHeader() {
  const { t } = useTranslation('common');
  const { user, loading, signOut } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const dashboardLabel = isPlayerApp()
    ? t('header.dashboard_player')
    : t('header.dashboard_director');

  // Tenant branding in the header. White-label tenants show their org
  // logo + name instead of the Crown + "Swiss Magic". Name precedence:
  // live org profile > build-time default > i18n generic. To avoid a
  // default-then-brand flash we mirror HomePage: render the name as soon
  // as a build-time value exists, and gate the logo on the fetch (a
  // Crown→logo swap is worse than a brief empty slot — there's no static
  // fallback for the uploaded logo URL). The public tenant resolves
  // loading=false synchronously, so it just renders Crown + name.
  const { profile: orgProfile, loading: orgLoading } = useOrgProfile();
  const brandName = orgProfile?.name?.trim() || BUILD_APP_NAME || t('app.name');
  const brandLogoUrl = orgProfile?.logoUrl ?? null;
  const brandNameReady = BUILD_APP_NAME.length > 0 || !orgLoading;
  const brandLogoReady = !orgLoading;

  // Story 063 + 064 — tenant-aware nav. On white-label tenants the
  // per-director profile is replaced by the org profile (story 064),
  // and org admins get extra nav items for the admin dashboards
  // (story 063). Both flags pulled from sources that are always
  // available client-side: tenant config from window.location.hostname,
  // is_org_admin from the JWT's app_metadata.
  const tenant = currentTenantOrNull();
  const isWhiteLabel = tenant?.isWhiteLabel ?? false;
  const allowsSelfRegistration = tenant?.allowsSelfRegistration ?? true;
  // Return the visitor to the page they were on after signing in, instead
  // of dropping them on the dashboard. LoginPage consumes ?redirect=.
  const loginHref = `/login?redirect=${encodeURIComponent(
    `${location.pathname}${location.search}`,
  )}`;
  // Org-admin role is a director-area concept (manages directors,
  // edits the org public profile). When a dual-role user (org admin
  // who also has a player profile) is on the player surface, these
  // entries must be hidden — the player area is an entirely separate
  // notification + navigation context, and director-area items leaking
  // there breaks the cross-role separation we promise.
  const isOrgAdmin =
    !isPlayerApp() &&
    (user?.app_metadata as { is_org_admin?: boolean } | undefined)
      ?.is_org_admin === true;
  // The director-profile page is the per-director public attribution
  // surfaced on tournament pages — not relevant on white-label tenants
  // where the org profile takes over. On the player surface the
  // "Profile" link goes to the player profile, which is always relevant.
  const showProfileLink = !isWhiteLabel || isPlayerApp();

  // Story 065 — "Install app" entry on the player surface; recovery path
  // for users who dismissed the persistent banner. Hidden when already
  // installed (display-mode: standalone). Click degrades gracefully to an
  // instructions modal when the browser can't fire a native prompt.
  const installState = useInstall();
  const brand = useBrandName();
  const [installModalOpen, setInstallModalOpen] = useState(false);
  // Story 065 followup — same touch-primary gate as the install banner:
  // desktop users have the browser's address-bar install icon and
  // don't need our menu affordance.
  const showInstallEntry =
    isPlayerApp() && !installState.installed && installState.touchPrimary;
  const handleInstallClick = async () => {
    const outcome = await installState.install();
    if (outcome === 'instructions-modal') {
      setInstallModalOpen(true);
    } else if (outcome === 'accepted') {
      showSuccessToast(t('header.install.installed_toast', { brand }));
    }
  };

  // Story 066 — Push notification entries on the player surface.
  // - "Enable notifications" visible when permission is default + supported
  //   (recovery path mirroring Install app).
  // - "Disable notifications" + "Send test notification" visible when
  //   the user is actively subscribed.
  const push = usePushSubscription();
  const pushSupported = push.permission !== 'unsupported';
  const showEnablePushEntry =
    isPlayerApp() &&
    pushSupported &&
    push.permission !== 'denied' &&
    !push.subscribed &&
    (!push.ios || push.installed);
  const showDisablePushEntry =
    isPlayerApp() && pushSupported && push.subscribed;
  const showTestPushEntry =
    isPlayerApp() &&
    pushSupported &&
    push.permission === 'granted' &&
    push.subscribed;

  const handleEnablePush = async () => {
    const r = await push.enable();
    if (r === 'enabled') showSuccessToast(t('header.notifications.enabled_toast'));
    else if (r === 'permission-denied') showErrorToast(t('header.notifications.permission_denied_toast'));
    else if (r === 'no-vapid-key') showErrorToast(t('header.notifications.no_vapid_key_toast'));
    else if (r === 'ios-needs-install') showErrorToast(t('header.notifications.ios_needs_install_toast'));
    else if (r === 'unsupported') showErrorToast(t('header.notifications.unsupported_toast'));
    else showErrorToast(t('header.notifications.enable_failed_toast'));
  };

  const handleDisablePush = async () => {
    const r = await push.disable();
    if (r === 'disabled' || r === 'not-subscribed') {
      showSuccessToast(t('header.notifications.disabled_toast'));
    } else {
      showErrorToast(t('header.notifications.disable_failed_toast'));
    }
  };

  const handleTestPush = async () => {
    try {
      const res = await sendTestPush();
      showSuccessToast(
        res.sent > 0
          ? t('header.notifications.test_sent_toast')
          : t('header.notifications.no_subscriptions_toast'),
      );
    } catch {
      showErrorToast(t('header.notifications.test_failed_toast'));
    }
  };

  return (
    <header
      data-testid="app-header"
      className="border-b bg-card px-4 py-3 md:px-6 md:py-4"
    >
      <div className="mx-auto flex max-w-4xl items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          {brandLogoReady &&
            (brandLogoUrl ? (
              <img
                data-testid="header-org-logo"
                src={brandLogoUrl}
                alt={brandName}
                className="h-7 w-auto max-w-[8rem] shrink-0 object-contain"
              />
            ) : (
              <Crown className="h-6 w-6 shrink-0 text-accent-foreground" />
            ))}
          {brandNameReady && (
            <h1
              data-testid="header-brand-name"
              className="hidden md:block text-lg md:text-xl font-semibold truncate"
            >
              {brandName}
            </h1>
          )}
        </div>

        {user && (
          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              data-testid="header-dashboard-shortcut"
              aria-label={dashboardLabel}
              className="gap-1 md:gap-2"
              onClick={() => navigate(postLoginPath())}
            >
              <LayoutDashboard className="h-4 w-4" />
              <span className="hidden md:inline">{dashboardLabel}</span>
            </Button>

            {currentSurface() === 'player' && <MessagesLink />}

            <NotificationsBell />

            {/* Story 070b — compact language picker; only the Globe
                icon to save horizontal space on mobile. */}
            <LocalePicker compact />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="user-menu-button"
                  className="gap-1 md:gap-2"
                >
                  <UserCircle className="h-5 w-5 md:hidden" />
                  <span
                    data-testid="user-email"
                    className="hidden md:inline text-sm text-muted-foreground max-w-[220px] truncate"
                  >
                    {user.email}
                  </span>
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" data-testid="user-menu">
                <DropdownMenuItem
                  data-testid="user-menu-dashboard"
                  onClick={() => navigate(postLoginPath())}
                >
                  <LayoutDashboard className="mr-2 h-4 w-4" />
                  {dashboardLabel}
                </DropdownMenuItem>
                {showProfileLink && (
                  <DropdownMenuItem
                    data-testid="user-menu-profile"
                    onClick={() => navigate(profilePath())}
                  >
                    <User className="mr-2 h-4 w-4" />
                    {t('header.user_menu.profile')}
                  </DropdownMenuItem>
                )}
                {/* Story 078 — director-private templates. Director
                    surface only (players don't create tournaments). */}
                {!isPlayerApp() && (
                  <DropdownMenuItem
                    data-testid="user-menu-templates"
                    onClick={() => navigate('/templates')}
                  >
                    <LayoutTemplate className="mr-2 h-4 w-4" />
                    {t('header.user_menu.templates')}
                  </DropdownMenuItem>
                )}
                {isOrgAdmin && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      data-testid="user-menu-org-profile"
                      onClick={() => navigate('/admin/organization')}
                    >
                      <Building2 className="mr-2 h-4 w-4" />
                      {t('header.user_menu.org_profile')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="user-menu-directors"
                      onClick={() => navigate('/admin/directors')}
                    >
                      <Users className="mr-2 h-4 w-4" />
                      {t('header.user_menu.directors')}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      data-testid="user-menu-fide-list"
                      onClick={() => navigate('/admin/fide-list')}
                    >
                      <ListChecks className="mr-2 h-4 w-4" />
                      {t('header.user_menu.fide_list')}
                    </DropdownMenuItem>
                  </>
                )}
                {showInstallEntry && (
                  <DropdownMenuItem
                    data-testid="user-menu-install-app"
                    onClick={handleInstallClick}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    {t('header.user_menu.install_app')}
                  </DropdownMenuItem>
                )}
                {showEnablePushEntry && (
                  <DropdownMenuItem
                    data-testid="user-menu-enable-notifications"
                    onClick={handleEnablePush}
                  >
                    <Bell className="mr-2 h-4 w-4" />
                    {t('header.user_menu.enable_notifications')}
                  </DropdownMenuItem>
                )}
                {showDisablePushEntry && (
                  <DropdownMenuItem
                    data-testid="user-menu-disable-notifications"
                    onClick={handleDisablePush}
                  >
                    <BellOff className="mr-2 h-4 w-4" />
                    {t('header.user_menu.disable_notifications')}
                  </DropdownMenuItem>
                )}
                {showTestPushEntry && (
                  <DropdownMenuItem
                    data-testid="user-menu-test-notification"
                    onClick={handleTestPush}
                  >
                    <Send className="mr-2 h-4 w-4" />
                    {t('header.user_menu.test_notification')}
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="user-menu-logout"
                  onClick={signOut}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('header.user_menu.sign_out')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}

        {!user && !loading && (
          <div className="flex items-center gap-1 md:gap-2 shrink-0">
            <Button variant="ghost" size="sm" asChild>
              <Link to={loginHref} data-testid="header-sign-in">
                {t('buttons.sign_in')}
              </Link>
            </Button>
            {allowsSelfRegistration && (
              <Button size="sm" asChild>
                <Link to="/register" data-testid="header-register">
                  {t('buttons.register')}
                </Link>
              </Button>
            )}
          </div>
        )}
      </div>
      <InstallInstructionsModal
        open={installModalOpen}
        brand={brand}
        onClose={() => setInstallModalOpen(false)}
      />
    </header>
  );
}
